import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Decision } from "../../../api/access/decisions";
import { useTabTitle } from "../../../shell/workspace";
import { formatCollectionVersionRef, formatDocumentVersionRef, type CollectionVersionRef, type DocumentVersionRef } from "../../../shared/references/contentReferences";
import AppButton from "../../../ui/buttons/AppButton";
import CopyReference from "../../../ui/content/CopyReference";
import ActionBar from "../../../ui/layout/ActionBar";
import DocumentVersionLabel from "./DocumentVersionLabel";

interface DocumentHeaderProps {
  reference: DocumentVersionRef;
  /** The document's name from the import; `null` while the document is loading. */
  documentKey: string | null;
  /** A segment is in focus: the start action works on it (F27). */
  segmentFocused: boolean;
  /** The latest version of the corpus that holds the document; `null` while unknown. A new one asks the version label again (Sol r1 and r2). */
  corpusVersion: CollectionVersionRef | null;
  /** `null` while there is nothing to start work on yet: no project chosen, or its corpus not loaded. */
  onStartThread: (() => void) | null;
  /** The chosen project's decision on starting work (`propose_change`), which the start action shows. */
  starting: Decision;
}

/** Title and actions of a document page; its actions show whether or not the document has loaded, its title once it has. */
export default function DocumentHeader({ reference, documentKey, corpusVersion, segmentFocused, onStartThread, starting }: DocumentHeaderProps) {
  const { t } = useTranslation();
  useTabTitle(documentKey === null ? null : { kind: "document", name: documentKey });
  // The corpus's latest version last known: the version label asks again when it changes, not
  // while the corpora list reloads and the version is briefly unknown (Sol r2).
  const [corpusKey, setCorpusKey] = useState("");
  const known = corpusVersion === null ? null : formatCollectionVersionRef(corpusVersion);
  if (known !== null && known !== corpusKey) setCorpusKey(known);

  return (
    <header className="px-4 pt-4 sm:px-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Named once its key is read; the page below says it is loading (P-F: no ID in its place). */}
          {documentKey === null ? null : <h2 className="app-text text-xl font-bold">{t("sources.document.title", { documentKey })}</h2>}
          <DocumentVersionLabel key={corpusKey} reference={reference} />
          <CopyReference value={formatDocumentVersionRef(reference)} />
        </div>
        <ActionBar>
          {onStartThread === null ? null : (
            <AppButton type="button" variant="primary" size="sm" requires={starting} onClick={onStartThread}>
              {t(segmentFocused ? "sources.document.startThreadOnSegment" : "sources.document.startThread")}
            </AppButton>
          )}
        </ActionBar>
      </div>
    </header>
  );
}
